import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 2rem;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-style: italic;
  margin-bottom: 3rem;
`;

const Content = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const SubSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.8rem;
  font-weight: 600;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 2rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const HighlightBox = styled.div`
  background: ${props => props.theme.colors.background};
  border-left: 4px solid ${props => props.theme.colors.primary};
  padding: 1.5rem;
  margin: 1.5rem 0;
  border-radius: 0 8px 8px 0;
`;

const TermsConditionsPage = () => {
  return (
    <Container>
      <Title>Terms & Conditions</Title>
      <LastUpdated>Last updated: July 22, 2025</LastUpdated>
      
      <Content>
        <Section>
          <Paragraph>
            Welcome to RPL Support. These Terms and Conditions govern your use of our website and services. 
            By accessing or using our services, you agree to be bound by these terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Definitions</SectionTitle>
          <SubSection>
            <Paragraph>
              <strong>"RPL Support," "we," "us," "our"</strong> refers to RPL Support, a provider of Recognition of Prior Learning services.
            </Paragraph>
            <Paragraph>
              <strong>"Services"</strong> refers to our RPL assessment, consultation, and qualification services.
            </Paragraph>
            <Paragraph>
              <strong>"Client," "you," "your"</strong> refers to any individual or organization using our services.
            </Paragraph>
            <Paragraph>
              <strong>"RPL"</strong> refers to Recognition of Prior Learning, the process of assessing and recognizing existing skills and knowledge.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>2. Services Description</SectionTitle>
          <SubSection>
            <Paragraph>
              RPL Support provides Recognition of Prior Learning services, including:
            </Paragraph>
            <List>
              <ListItem>Skills assessment and qualification matching</ListItem>
              <ListItem>Portfolio preparation assistance</ListItem>
              <ListItem>Professional assessment services</ListItem>
              <ListItem>Gap analysis and training recommendations</ListItem>
              <ListItem>Qualification pathway guidance</ListItem>
            </List>
            <Paragraph>
              We partner with Registered Training Organisations (RTOs) to provide nationally recognised qualifications.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>3. Client Responsibilities</SectionTitle>
          <SubSection>
            <SubSectionTitle>3.1 Accurate Information</SubSectionTitle>
            <Paragraph>
              You must provide accurate, complete, and truthful information throughout the RPL process. 
              Any false or misleading information may result in disqualification from our services.
            </Paragraph>
          </SubSection>
          
          <SubSection>
            <SubSectionTitle>3.2 Documentation</SubSectionTitle>
            <Paragraph>
              You are responsible for providing all necessary documentation and evidence to support your RPL application. 
              This includes work history, certificates, references, and any other relevant materials.
            </Paragraph>
          </SubSection>
          
          <SubSection>
            <SubSectionTitle>3.3 Cooperation</SubSectionTitle>
            <Paragraph>
              You must cooperate fully with our assessors and staff throughout the RPL process, 
              including attending scheduled meetings and responding to requests for additional information.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>4. Fees and Payment</SectionTitle>
          <SubSection>
            <SubSectionTitle>4.1 Service Fees</SubSectionTitle>
            <Paragraph>
              All fees are quoted in Australian dollars and are subject to change without notice. 
              Fees are non-refundable once the assessment process has commenced.
            </Paragraph>
          </SubSection>
          
          <SubSection>
            <SubSectionTitle>4.2 Payment Terms</SubSectionTitle>
            <Paragraph>
              Payment is required in full before services commence, unless otherwise agreed in writing. 
              We accept payment by bank transfer, credit card, or other methods as specified.
            </Paragraph>
          </SubSection>
          
          <SubSection>
            <SubSectionTitle>4.3 Refunds</SubSectionTitle>
            <Paragraph>
              Refunds may be provided at our discretion in exceptional circumstances. 
              No refunds will be provided if the client fails to cooperate or provide required documentation.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>5. Assessment Process</SectionTitle>
          <SubSection>
            <SubSectionTitle>5.1 Assessment Timeline</SubSectionTitle>
            <Paragraph>
              The RPL assessment process typically takes 4-8 weeks, depending on the complexity of the qualification 
              and the completeness of provided documentation.
            </Paragraph>
          </SubSection>
          
          <SubSection>
            <SubSectionTitle>5.2 Assessment Outcomes</SubSectionTitle>
            <Paragraph>
              Assessment outcomes are determined by qualified assessors based on evidence provided. 
              We cannot guarantee specific outcomes, as these depend on the quality and relevance of your evidence.
            </Paragraph>
          </SubSection>
          
          <SubSection>
            <SubSectionTitle>5.3 Appeals Process</SubSectionTitle>
            <Paragraph>
              If you disagree with an assessment outcome, you may appeal in writing within 30 days. 
              Appeals will be reviewed by an independent assessor.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>6. Privacy and Confidentiality</SectionTitle>
          <SubSection>
            <Paragraph>
              We are committed to protecting your privacy and maintaining confidentiality. 
              All personal information is handled in accordance with our Privacy Policy and applicable privacy laws.
            </Paragraph>
            <Paragraph>
              Your personal information and assessment materials will only be shared with:
            </Paragraph>
            <List>
              <ListItem>Our qualified assessors</ListItem>
              <ListItem>Partner RTOs (as required for qualification issuance)</ListItem>
              <ListItem>Regulatory bodies (as required by law)</ListItem>
            </List>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>7. Intellectual Property</SectionTitle>
          <SubSection>
            <Paragraph>
              All content on this website, including text, graphics, logos, and software, 
              is the property of RPL Support and is protected by copyright laws.
            </Paragraph>
            <Paragraph>
              You may not reproduce, distribute, or create derivative works without our written permission.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>8. Limitation of Liability</SectionTitle>
          <SubSection>
            <Paragraph>
              RPL Support's liability is limited to the amount paid for our services. 
              We are not liable for any indirect, consequential, or incidental damages.
            </Paragraph>
            <Paragraph>
              We do not guarantee employment outcomes or career advancement as a result of obtaining qualifications.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>9. Dispute Resolution</SectionTitle>
          <SubSection>
            <Paragraph>
              Any disputes arising from these terms or our services will be resolved through:
            </Paragraph>
            <List>
              <ListItem>Direct negotiation with our management team</ListItem>
              <ListItem>Mediation (if required)</ListItem>
              <ListItem>Legal proceedings in Australian courts (as a last resort)</ListItem>
            </List>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>10. Changes to Terms</SectionTitle>
          <SubSection>
            <Paragraph>
              We reserve the right to modify these terms at any time. 
              Changes will be effective immediately upon posting on our website.
            </Paragraph>
            <Paragraph>
              Continued use of our services after changes constitutes acceptance of the new terms.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>11. Governing Law</SectionTitle>
          <SubSection>
            <Paragraph>
              These terms are governed by the laws of Australia. 
              Any legal proceedings must be brought in Australian courts.
            </Paragraph>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>12. Contact Information</SectionTitle>
          <SubSection>
            <Paragraph>
              For questions about these terms or our services, please contact us:
            </Paragraph>
            <HighlightBox>
              <Paragraph><strong>Email:</strong> info@rplsupport.com.au</Paragraph>
              <Paragraph><strong>Phone:</strong> 1300 RPL HELP</Paragraph>
              <Paragraph><strong>Address:</strong> [Your Business Address]</Paragraph>
            </HighlightBox>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>13. Acceptance</SectionTitle>
          <SubSection>
            <Paragraph>
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </Paragraph>
            <HighlightBox>
              <Paragraph>
                <strong>Important:</strong> These terms constitute a legally binding agreement between you and RPL Support. 
                If you do not agree to these terms, please do not use our services.
              </Paragraph>
            </HighlightBox>
          </SubSection>
        </Section>
      </Content>
    </Container>
  );
};

export default TermsConditionsPage; 